import { useEffect, useMemo, useState } from 'react';
import { useStripe } from '@stripe/react-stripe-js';
import { connect } from 'react-redux';
import { X } from 'react-feather';

import API from '../../api';
import { mapDispatchProps, mapStateToProps } from '../../store/store';
import { BRAND_BILLING_TYPES } from 'store/brand/reducer';

import CardSelect from '../../pages/cards/select';
import Button from '../../components/button';
import Checkbox, { ICON_TYPES } from '../../components/checkbox';
import RadioButton from 'components/radioButton';

import { useLanguage } from '@languages/index';
import { getCurrencyMultiplier } from 'helpers/currency';
import { debounce } from '../../helpers/debounce';
import SelectCommission from 'components/selectCommission/selectCommission';
import { PaymentProductType } from 'types/payment';
import { PAYMENT_PRODUCT_TYPE } from 'constants/payment';
import { OverlayBrandTaxInvoiceInfoFormResult } from 'components/overlayBrandTaxInvoiceInfoForm';

export interface PaymentCreateBeautyProps {
  summary?: any;

  temporaryKey?: string;
  campaignId?: any;
  productId?: any;

  campaign?: any;
  // handleChange?: any; // DEPRECATED/250527

  itemType: PaymentProductType;
  amount: number;
  price: number;
  priceTotal?: number; // 원고료 제외 금액 (원래 이 컴포넌트에서 옵션 변경하면서 계산했음 -> 고정값으로 변경된 상태)

  priceCommission?: null|number; // 이 값이 없을 경우 store.campaign.commissionAmount 사용(=구 패턴 - campaign.commissionAmount와 campaign_payments.price가 서비스국가, 브랜드결제통화에 따라 다른데 반영 못하는 문제 있음)

  cashCampaignPaymentData?: Pick<
    OverlayBrandTaxInvoiceInfoFormResult['data']['campaignPayment'], 
    keyof OverlayBrandTaxInvoiceInfoFormResult['data']['campaignPayment']
  > | null

  brand?: any;
  auth?: any;
  preferences?: any;
  settings?: any;

  onSuccess?: any;


  setPopup?: (params?: any) => void;
  closePopup?: () => void;

  showSheet?: (params?: any) => void;
  closeSheet?: () => void;

  showModal?: (params?: any) => void;
  closeModal?: () => void;

  showOverlays?: (params?: any) => void;
  closeOverlays?: () => void;
}

const PaymentCreateBeauty = (props: PaymentCreateBeautyProps) => {
  const stripe = useStripe();
  const language = useLanguage();
  const languageTexts = { ...language.common, ...language.pages.payments };

  const PRODUCT_DURATIONS_VALUE_SINGLE = 'month';
  const PRODUCT_DURATIONS_VALUE_YEARLY = 'year';

  const PRODUCT_DURATION_SINGLE = {
    value: PRODUCT_DURATIONS_VALUE_SINGLE,
    name: languageTexts.create_text_20,
    price: props.brand.priceSeeding,
  };

  const PRODUCT_DURATION_YEARLY = {
    value: PRODUCT_DURATIONS_VALUE_YEARLY,
    name: languageTexts.create_text_21,
    price: props.brand.priceSeedingPass,
  };

  const PRODUCT_DURATIONS = [PRODUCT_DURATION_SINGLE, PRODUCT_DURATION_YEARLY];

  const [isPending, setIsPending] = useState(false);
  const [isAgreed, setIsAgreed] = useState(true);

  const [itemType, setItemType] = useState(props.itemType);
  const [productDuration, setProductDuration] = useState(
    props.brand.isCategoryBeauty ? PRODUCT_DURATION_SINGLE : null,
  );
  const [amount, setAmount] = useState(props.amount);

  const getPrice = () => {
    if (props.price) return props.price;
    return PRODUCT_DURATION_SINGLE.price;
  };

  const [price, setPrice] = useState(getPrice());

  const getPriceTotal = () => {
    if (props.priceTotal) return props.priceTotal;
    return PRODUCT_DURATION_SINGLE.price * props.amount;
  };

  const [priceTotal, setPriceTotal] = useState(getPriceTotal());

  const priceCommission = useMemo(() => {
    return props.priceCommission ?? props.campaign.commissionAmount;
  }, [props.priceCommission, props.campaign.commissionAmount]);

  useEffect(() => {
    let amountToPay = 0;
    const hasSeedingPass = props.brand.remainSeedingPass;

    if (productDuration && props.brand.isCategoryBeauty) {
      switch (productDuration.value) {
        case PRODUCT_DURATIONS_VALUE_SINGLE:
          if (!hasSeedingPass)
            amountToPay += parseInt(PRODUCT_DURATION_SINGLE.price) * props.amount;
          if (priceCommission != null)
            amountToPay += parseInt(priceCommission) * props.amount;
          if (props.campaign?.inspectionSubmitDueDt != null) {
            amountToPay += getInspectionFee() * props.amount;
          }

          setPrice(PRODUCT_DURATION_SINGLE.price);
          setPriceTotal(amountToPay);
          if (props.brand.remainSeedingPass) {
            setItemType(
              PAYMENT_PRODUCT_TYPE.SEEDING_PASS_BEAUTY_COMMISSION_ONLY,
            );
          } else {
            setItemType(PAYMENT_PRODUCT_TYPE.SEEDING_PASS_BEAUTY_SINGLE);
          }
          break;

        case PRODUCT_DURATIONS_VALUE_YEARLY:
          if (!hasSeedingPass)
            amountToPay += parseInt(PRODUCT_DURATION_YEARLY.price);
          if (priceCommission)
            amountToPay += parseInt(priceCommission) * props.amount;

          setPrice(PRODUCT_DURATION_YEARLY.price);
          setPriceTotal(amountToPay);
          setItemType(PAYMENT_PRODUCT_TYPE.SEEDING_PASS_BEAUTY_YEARLY);
          break;
      }
    }
  }, [productDuration, props.campaign, priceCommission]);

  useEffect(() => {
    // DEBUG
    let priceInspection = props.campaign?.inspectionSubmitDueDt != null ? getInspectionFee() : 0;
    let calculation = price * amount + priceCommission * amount + priceInspection * amount;
    let debugValues = {
      price,
      amount,
      commissionAmount: priceCommission,
      priceInspection,
      priceTotal,
      calculation,
    };
    console.log('Debug values:', debugValues);
  }, [
    price,
    amount,
    priceTotal,
    priceCommission,
    props.campaign?.inspectionSubmitDueDt,
  ]);

  const getProductName = () => {
    let result = '';
    switch (itemType) {
      case PAYMENT_PRODUCT_TYPE.SEEDING_FIRST:
        result = languageTexts.create_text_2;

      case PAYMENT_PRODUCT_TYPE.SEEDING_PASS:
        result = languageTexts.create_text_3;
        break;

      case PAYMENT_PRODUCT_TYPE.SEEDING_PASS_RECOVER:
        result = languageTexts.create_text_4;
        break;

      case PAYMENT_PRODUCT_TYPE.SEEDING:
        result = languageTexts.create_text_5;
        break;

      case PAYMENT_PRODUCT_TYPE.SEEDING_COMMISSION:
        result = languageTexts.create_text_16;
        break;

      case PAYMENT_PRODUCT_TYPE.SNS_PASS:
      case PAYMENT_PRODUCT_TYPE.SNS_INSTALL:
        result = languageTexts.create_text_6;
        break;

      case PAYMENT_PRODUCT_TYPE.SNS_PASS_RECOVER:
        result = languageTexts.create_text_7;
        break;

      case PAYMENT_PRODUCT_TYPE.SEEDING_PASS_BEAUTY_SINGLE:
      case PAYMENT_PRODUCT_TYPE.SEEDING_PASS_BEAUTY_YEARLY:
      case PAYMENT_PRODUCT_TYPE.SEEDING_PASS_BEAUTY_COMMISSION_ONLY:
        result = languageTexts.create_text_17;
        break;
    }
    return result;
  };

  const getPaymentDetails = () => {
    let details = [];
    if (priceCommission) {
      details.push({
        name: languageTexts.create_text_26,
        description: `${priceCommission.toLocaleString()}${props.settings.currencyText} * ${languageTexts._amount(amount)}`,
      });
    }

    // 메인 상품
    if (
      productDuration &&
      productDuration.value == PRODUCT_DURATION_YEARLY.value
    ) {
      details.push({
        name: languageTexts.create_text_21,
        description: `${parseInt(
          props.brand.priceSeedingPass,
        ).toLocaleString()}${props.settings.currencyText}`,
      });
    } else if (
      productDuration == null &&
      itemType === PAYMENT_PRODUCT_TYPE.SEEDING_PASS
    ) {
      details.push({
        name: languageTexts.create_text_25,
        description: `${parseInt(
          props.brand.priceSeedingPass,
        ).toLocaleString()}${props.settings.currencyText}`,
      });
    } else if (getProductPrice() != languageTexts.free) {
      details.push({
        name: languageTexts.create_text_17,
        description: `${getProductPrice().toLocaleString()} * ${languageTexts._amount(
          amount,
        )}`,
      });
    }

    if (props.campaign?.inspectionSubmitDueDt != null) {
      details.push({
        name: languageTexts.create_text_27,
        description: `${getInspectionFeeText()} * ${languageTexts._amount(
          amount,
        )}`,
      });
    }
    return details;
  };

  const renderTitle = () => {
    return (
      <div className="flex flex-row items-center md:pt-[50px] pb-[30px]">
        <div className="flex flex-row justify-center items-center">
          <span className="text-xl font-bold text-center md:text-start">
            {languageTexts.pay}
          </span>
        </div>

        <div
          className="cursor-pointer items-center absolute right-0 top-0 m-[30px]"
          onClick={() => {
            props.closeOverlays();
          }}
        >
          <div>
            <X size={24} strokeWidth={1} />
          </div>
        </div>
      </div>
    );
  };

  const getProductPrice = () => {
    if (
      productDuration &&
      (props.brand.remainSeedingPass ||
        productDuration.value == PRODUCT_DURATION_YEARLY.value)
    ) {
      return languageTexts.free;
    }

    return languageTexts._price(
      parseInt(price).toLocaleString(),
      props.settings.currencyText,
    );
  };

  const getInspectionFee = () => {
    const currencyMultiplier = getCurrencyMultiplier(props.brand.currency);
    return (
      parseInt(
        props.brand?.priceInspection != null
          ? props.brand.priceInspection
          : props.settings?.priceInspection ?? 0,
      ) * currencyMultiplier
    );
  };

  const getInspectionFeeText = () => {
    return languageTexts._price(
      getInspectionFee().toLocaleString(),
      props.settings.currencyText,
    );
  };

  const getProductPrefix = () => {
    const isGettingPaidPerEach = [
      PAYMENT_PRODUCT_TYPE.SEEDING,
      PAYMENT_PRODUCT_TYPE.SEEDING_COMMISSION,
      PAYMENT_PRODUCT_TYPE.SEEDING_PASS_BEAUTY_SINGLE,
    ].includes(itemType);

    if (getProductPrice() == languageTexts.free) return '';
    if (isGettingPaidPerEach) return `${languageTexts.create_text_per_each} `;
    return '';
  };

  const getPriceTotalWithCommissions = () => {
    let recalculatedPriceTotal = price * amount;
    if (priceCommission != null) {
      recalculatedPriceTotal += priceCommission * amount;
    }
    if (props.campaign?.inspectionSubmitDueDt != null) {
      recalculatedPriceTotal += getInspectionFee() * amount;
    }

    return priceTotal
      ? Math.round(priceTotal)
      : Math.round(recalculatedPriceTotal);
  };

  const getPriceTotalWithCommissionsAndTax = () => {
    return Math.round(getPriceTotalWithCommissions() * 1.1);
  };

  const renderSummary = () => {
    return (
      <>
        <div className="flex flex-col">
          <div className="flex flex-col">
            <div className="flex flex-col gap-[15px]">
              {/* 동동이 이용권 */}
              {itemType == PAYMENT_PRODUCT_TYPE.SNS_INSTALL ? (
                <div className="flex flex-row justify-between">
                  <div className="flex flex-col justify-center">
                    <span className="font-bold text-[15px] tracking-tighter max-w-[115px]">
                      {itemType == PAYMENT_PRODUCT_TYPE.SNS_INSTALL
                        ? languageTexts.complete_text_12
                        : null}
                    </span>
                  </div>

                  <div className="flex flex-col justify-center gap-1">
                    <span className="text-[15px] tracking-tighter text-right">
                      {languageTexts._price(
                        props.brand.priceSnsInstall.toLocaleString(),
                        props.settings.currencyText,
                      )}
                    </span>

                    {itemType == PAYMENT_PRODUCT_TYPE.SNS_INSTALL ? (
                      <span className="text-[11px] tracking-tighter text-right text-gray-400">
                        {languageTexts.complete_text_13}
                      </span>
                    ) : null}
                  </div>
                </div>
              ) : null}

              {/* 뷰티 연간 이용권 */}
              {productDuration &&
              productDuration.value == PRODUCT_DURATION_YEARLY.value ? (
                <div className="flex flex-col">
                  <div className="flex flex-row justify-between">
                    <div className="flex flex-col justify-center">
                      <span className="font-bold text-[15px] tracking-tighter">
                        {languageTexts.create_text_21}
                      </span>
                    </div>
                    <div className="flex flex-col justify-center gap-1">
                      <span className="text-[15px] tracking-tighter text-right">
                        {languageTexts._price(
                          Math.round(
                            PRODUCT_DURATION_YEARLY.price,
                          ).toLocaleString(),
                          props.settings.currencyText,
                        )}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-row mt-[25px]">
                    <div className="border-t border-[#E8E8E8] w-full" />
                  </div>
                </div>
              ) : null}

              {/* [DEPRECATED/250527] 결제모달에서 원고료 선택가능했던 기능 */}
              {/* {props.brand.isCategoryBeauty && (
                <dl className="flex flex-col gap-[10px]">
                  <dt className="font-bold text-[15px] tracking-tighter">{`${languageTexts.create_text_26}`}</dt>
                  <dd>
                    <SelectCommission
                      brand={props.brand}
                      settings={props.settings}
                      selectedValue={props.campaign?.commissionAmount} // 구버전 방식이라 재사용하려면 개조해야함
                      onChange={(value) =>
                        props.handleChange(`commissionAmount`, value)
                      }
                    />
                  </dd>
                </dl>
              )} */}

              {/* 원고료 결제 비용 */}
              {priceCommission ? (
                <div className="flex flex-row justify-between">
                  <div className="flex flex-col">
                    <span className="font-bold text-[15px] tracking-tighter max-w-[115px]">
                      {`${languageTexts.create_text_26}`}
                    </span>
                  </div>

                  <div>
                    <span className="text-[15px] tracking-tighter text-right">
                      {`${languageTexts.create_text_per_each} ${languageTexts._price(
                        parseInt(priceCommission).toLocaleString(),
                        props.settings.currencyText,
                      )}`}
                    </span>
                  </div>
                </div>
              ) : null}

              <div className="flex flex-row justify-between">
                <div className="flex flex-col justify-center">
                  <span className="font-bold text-[15px] tracking-tighter max-w-[115px]">{`${getProductName()}`}</span>
                </div>

                <div className="flex flex-col justify-center gap-1">
                  <span className="text-[15px] tracking-tighter text-right">{`${getProductPrefix()}${getProductPrice()}`}</span>

                  {/* 제품제공 1회용
                  {itemType == PAYMENT_PRODUCT_TYPE.SEEDING ? (
                    <span className="text-[11px] tracking-tighter text-right text-gray-400">
                      {languageTexts._complete_text_total_price(intoKorean(price), amount)}
                    </span>
                  ) : null} */}

                  {/* 제품 제공 자동 결제 복구 */}
                  {itemType == PAYMENT_PRODUCT_TYPE.SEEDING_PASS_RECOVER ? (
                    <span className="text-[11px] tracking-tighter text-right text-gray-400">
                      {languageTexts._complete_text_14(
                        props.brand.activeSeedingPassEndsAt,
                      )}
                    </span>
                  ) : null}

                  {/* 동동이 이용권 */}
                  {itemType == PAYMENT_PRODUCT_TYPE.SNS_INSTALL ||
                  itemType == PAYMENT_PRODUCT_TYPE.SNS_PASS ? (
                    <span className="text-[11px] tracking-tighter text-right text-gray-400">
                      {languageTexts._create_text_10(
                        props.settings.durationSnsPass,
                      )}
                    </span>
                  ) : null}
                </div>
              </div>

              {/* 사전 검수 비용 */}
              {props?.campaign?.inspectionSubmitDueDt != null && (
                <div className="flex flex-row justify-between">
                  <div className="flex flex-col justify-center">
                    <span className="font-bold text-[15px] tracking-tighter max-w-[115px]">
                      {languageTexts.create_text_27}
                    </span>
                  </div>
                  <div className="flex flex-col justify-center gap-1">
                    <span className="text-[15px] tracking-tighter text-right">{`${getProductPrefix()}${getInspectionFeeText()}`}</span>
                  </div>
                </div>
              )}

              <div className="flex flex-row justify-between items-center">
                <div className="flex flex-col justify-center">
                  <span className="font-bold text-[15px] tracking-tighter">
                    {languageTexts.create_text_18}
                  </span>
                </div>

                <div className="flex flex-col justify-center gap-1">
                  <span className="text-[15px] tracking-tighter text-right">
                    {languageTexts.create_text_total_amount(amount)}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex flex-row my-[25px]">
              <div className="border-t border-[#E8E8E8] w-full" />
            </div>

            <div className="flex flex-row justify-between">
              <div className="flex flex-col"></div>
              <div className="flex flex-col">
                <div className="flex flex-row">
                  <div className="flex flex-col">
                    {/* {`(`} */}

                    {getPaymentDetails().map((item, index) => {
                      return (
                        <span
                          key={index}
                          className="text-[15px] text-[#999999] text-right"
                        >
                          {`${item.name} `}
                          {`${item.description}`}
                        </span>
                      );
                    })}

                    {/* {`)`} */}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <div className="flex flex-row justify-between">
                <div className="flex flex-col justify-center">
                  <span className="font-bold text-[15px] tracking-tighter">
                    {languageTexts.create_text_19}
                  </span>
                </div>
                <div className="flex flex-col justify-center gap-1">
                  <span className="text-[15px] tracking-tighter text-right">
                    {languageTexts._price(
                      getPriceTotalWithCommissions().toLocaleString(),
                      props.settings.currencyText,
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-row my-[25px]">
            <div className="border-t border-black w-full" />
          </div>

          <div className="flex flex-row justify-between">
            <span className="font-bold text-[15px] tracking-tighter">
              {languageTexts.complete_text_17}
            </span>
            <span className="font-bold text-[22px] tracking-tighter">
              {languageTexts._price(
                getPriceTotalWithCommissionsAndTax().toLocaleString(),
                props.settings.currencyText,
              )}
            </span>
          </div>
        </div>
      </>
    );
  };

  const renderProceedPaymentButton = () => {
    const isActiveCardExists = props.brand.cards && props.brand.cards.length;
    const isFreeBrand = props.brand.billingType === BRAND_BILLING_TYPES.FREE;

    let buttonColor = '#919191';
    if ((isFreeBrand || isActiveCardExists) && isAgreed && !isPending)
      buttonColor = 'BLACK';

    const getText = () => {
      if (isPending) return languageTexts.create_text_13;
      if (!isAgreed) return languageTexts.create_text_14;

      return languageTexts.pay;
    };

    return (
      <div className="fixed md:relative md:w-auto bottom-0 left-0 right-0 form-submit-button">
        {isFreeBrand && (
          <div className="mb-[4px] text-center text-[14px] text-[#FF4040] font-bold">
            {languageTexts.create_text_cash_brand_warning}
          </div>
        )}
        <Button
          textColor={'WHITE'}
          buttonColor={buttonColor}
          borderColor={buttonColor}
          text={getText()}
          onClick={
            isPending || !isAgreed
              ? () => {}
              : () => {
                  API.Card.index({
                    ...{
                      brandToken: props.auth.token,
                    },
                  })
                    .then((response) => {
                      const fetchedCards = response.data.cards.data;
                      if (
                        !fetchedCards.length &&
                        props.brand.billingType != BRAND_BILLING_TYPES.FREE
                      ) {
                        props.showModal({ text: languageTexts.create_text_15 });
                        setIsPending(false);
                        return false;
                      }

                      if (isPending) return false;
                      setIsPending(true);

                      debounce(async () => {
                        try {
                          const response = await API.Payment.proceed({
                            temporaryKey: props.temporaryKey,
                            campaignId: props.campaignId,
                            productId: props.productId,
                            brandToken: props.auth.token,
                            itemType: itemType,
                            amount: amount,
                            price: getPrice(),
                            priceTotal: getPriceTotalWithCommissionsAndTax(), // 주의: 세금 포함
                            commissionAmount: priceCommission,
                            inspectionFee:
                              props.campaign?.inspectionSubmitDueDt != null
                                ? getInspectionFee()
                                : 0,
                            // 이하 현금결제관련
                            taxInvoiceScheduledDate: props.cashCampaignPaymentData?.taxInvoiceScheduledDate,
                            depositScheduledDate: props.cashCampaignPaymentData?.depositScheduledDate,
                          });

                          props.closeOverlays();
                          setTimeout(() => {
                            props.onSuccess
                              ? props.onSuccess({
                                  itemType: itemType,
                                  amount: amount,
                                  price: getPrice(),
                                  priceTotal: getPriceTotalWithCommissions(), // 주의: 세금 미포함
                                  payment: response.data.payment,
                                  paymentDetails: getPaymentDetails(),
                                  commissionAmount: priceCommission,
                                  inspectionFee: props.campaign?.inspectionSubmitDueDt != null ? getInspectionFee() : 0,
                                })
                              : null;
                          }, 400);

                          setTimeout(() => {
                            setIsPending(false);
                          }, 1000);
                        } catch (paymentResponse) {
                          if (paymentResponse.message == 'requires_action') {
                            setIsPending(false);
                            props.showModal({
                              text: '현재 이용할 수 없는 카드 입니다. 다른 카드를 이용해주세요.',
                            });
                            return false;

                            // const { error, paymentIntent } = await stripe.confirmCardPayment(
                            //   paymentResponse.data.payment_intent_client_secret
                            // )

                            // if (error) {
                            // props.showModal({ text: error.message })
                            //   return
                            // }

                            // The payment requires additional confirmation
                            // const confirmResponse = await fetch("/your-backend-confirm-route", {
                            //   method: "POST",
                            //   headers: {
                            //     "Content-Type": "application/json",
                            //   },
                            //   body: JSON.stringify({ paymentIntentId: paymentIntent.id }),
                            // })

                            // const confirmResult = await confirmResponse.json()

                            // if (confirmResult.error) {
                            //   console.error(confirmResult.error)
                            //   alert("Payment failed: " + confirmResult.error)
                            // } else {
                            //   alert("Payment successful!")
                            // }

                            return false;
                          }

                          setIsPending(false);
                          props.showModal({ text: paymentResponse.message });
                        }
                      }, 400);
                    })
                    .catch((error) => {
                      setTimeout(() => {
                        setIsPending(false);
                        props.showModal({ text: error.message });
                      }, 400);
                    });
                }
          }
        />
      </div>
    );
  };

  const renderCards = () => {
    return (
      <div className="flex flex-col w-full mt-[40px] min-w-[320px] max-w-[370px]">
        <CardSelect cardsContainerClassName="p-0" hidePageTitle={true} />
      </div>
    );
  };

  const renderTerms = () => {
    const isAgreeRequired =
      itemType == PAYMENT_PRODUCT_TYPE.SEEDING_PASS ||
      itemType == PAYMENT_PRODUCT_TYPE.SEEDING_PASS_BEAUTY_YEARLY;

    return (
      <>
        {isAgreeRequired ? (
          <div className="flex flex-row justify-between py-[22px] mx-auto">
            <div className="flex flex-col justify-center gap-1 items-end">
              <Checkbox
                text={languageTexts.create_text_12}
                textClassName={'text-[15px] tracking-tighter text-right'}
                type={ICON_TYPES.DEFAULT}
                checked={isAgreed}
                onClick={() => {
                  setIsAgreed(!isAgreed);
                }}
              />
            </div>
          </div>
        ) : (
          <div className="h-[40px]" />
        )}
      </>
    );
  };

  const renderActiveSeedingPass = () => {
    const showActiveSeedingPass = [
      PAYMENT_PRODUCT_TYPE.SEEDING_PASS_BEAUTY_SINGLE,
      PAYMENT_PRODUCT_TYPE.SEEDING_PASS_BEAUTY_YEARLY,
      PAYMENT_PRODUCT_TYPE.SEEDING_PASS_BEAUTY_COMMISSION_ONLY,
    ].includes(itemType);

    if (!showActiveSeedingPass) return null;
    if (!props.brand.activeSeedingPassEndsAt) return null;

    return (
      <div className="flex flex-col">
        <div className="flex flex-col items-center justify-center py-[15px] bg-[#F4F4F4]">
          <h3 className="text-[15px] font-bold mb-[5px]">
            {`${
              props.brand.isCategoryFashion ? languageTexts.create_text_25 : ''
            } `}
            {`${
              props.brand.isCategoryBeauty ? languageTexts.create_text_21 : ''
            } `}
            {`${languageTexts.create_text_pass_using}`}
          </h3>
          <p className="text-[15px] text-[#999999]">
            {`${props.brand.activeSeedingPassStartedAt} ~ ${props.brand.activeSeedingPassEndsAt}`}
          </p>
        </div>
        <div className="h-[40px]" />
      </div>
    );
  };

  const renderProductDurationSelector = () => {
    const isVisible = [
      PAYMENT_PRODUCT_TYPE.SEEDING_PASS_BEAUTY_SINGLE,
      PAYMENT_PRODUCT_TYPE.SEEDING,
      PAYMENT_PRODUCT_TYPE.SEEDING_PASS_BEAUTY_YEARLY,
      PAYMENT_PRODUCT_TYPE.SEEDING_PASS,
    ].includes(itemType);

    const isDurationSelectable = [
      // PAYMENT_PRODUCT_TYPE.SEEDING_PASS_BEAUTY_SINGLE,
      // PAYMENT_PRODUCT_TYPE.SEEDING_PASS_BEAUTY_YEARLY,
    ].includes(itemType);

    const isSeedingPass = [
      PAYMENT_PRODUCT_TYPE.SEEDING_PASS,
      PAYMENT_PRODUCT_TYPE.SEEDING_PASS_BEAUTY_YEARLY,
    ].includes(itemType);
    const isSeedingSingle = [
      PAYMENT_PRODUCT_TYPE.SEEDING,
      PAYMENT_PRODUCT_TYPE.SEEDING_PASS_BEAUTY_SINGLE,
    ].includes(itemType);

    if (!isVisible) return null;
    if (props.brand.activeSeedingPassEndsAt) return null;

    return (
      <div className="flex flex-col pb-[40px] gap-[15px]">
        {isDurationSelectable ? (
          <div className="flex flex-row">
            <RadioButton
              tabClassName="px-[10px] py-3"
              selectedClassName="px-[10px] py-3 bg-black font-bold"
              selectedTextColor="WHITE"
              options={PRODUCT_DURATIONS}
              selected={productDuration}
              onSelect={(option) => setProductDuration(option)}
            />
          </div>
        ) : null}

        {isSeedingPass ? (
          <div className="flex flex-row">
            <RadioButton
              tabClassName="px-[10px] py-3"
              selectedClassName="px-[10px] py-3 bg-black font-bold"
              selectedTextColor="WHITE"
              options={[
                {
                  name: languageTexts.create_text_25,
                  price: props.brand.priceSeedingPass,
                  value: itemType,
                },
              ]}
              selected={{
                name: languageTexts.create_text_25,
                price: props.brand.priceSeedingPass,
                value: itemType,
              }}
              onSelect={(option) => true}
            />
          </div>
        ) : null}

        {isSeedingSingle ? (
          <div className="flex flex-row">
            <RadioButton
              tabClassName="px-[10px] py-3"
              selectedClassName="px-[10px] py-3 bg-black font-bold"
              selectedTextColor="WHITE"
              options={[
                {
                  name: languageTexts.create_text_20,
                  price: props.brand.priceSeeding,
                  value: itemType,
                },
              ]}
              selected={{
                name: languageTexts.create_text_20,
                price: props.brand.priceSeeding,
                value: itemType,
              }}
              onSelect={(option) => true}
            />
          </div>
        ) : null}

        {itemType == PAYMENT_PRODUCT_TYPE.SEEDING_PASS_BEAUTY_SINGLE ||
        itemType == PAYMENT_PRODUCT_TYPE.SEEDING ? (
          <div className="flex flex-row justify-center items-center">
            <span className="text-[15px] text-[#999999]">
              {languageTexts.create_text_22}
            </span>
          </div>
        ) : null}

        {itemType == PAYMENT_PRODUCT_TYPE.SEEDING_PASS_BEAUTY_YEARLY ? (
          <div className="flex flex-row justify-center items-center">
            <span className="text-[15px] text-[#999999]">
              {languageTexts.create_text_23}
            </span>
          </div>
        ) : null}

        {itemType == PAYMENT_PRODUCT_TYPE.SEEDING_PASS ? (
          <div className="flex flex-row justify-center items-center">
            <span className="text-[15px] text-[#999999]">
              {languageTexts.create_text_24}
            </span>
          </div>
        ) : null}
      </div>
    );
  };

  let containerStyle = '';
  if (props.preferences.isMobile) {
    containerStyle =
      'h-[100dvh] flex flex-col overflow-scroll bg-white w-[100dvw] px-5 py-5';
  } else {
    containerStyle =
      'relative w-full flex flex-col rounded-t-2xl overflow-y-auto max-h-[100dvh] px-[50px] bg-white pb-[50px] w-[480px]';
  }

  return (
    <>
      <div className={`${containerStyle}`}>
        {renderTitle()}
        {renderActiveSeedingPass()}
        {/* {renderProductDurationSelector()} */}
        {renderSummary()}
        {renderCards()}
        {renderTerms()}
        {renderProceedPaymentButton()}
      </div>
    </>
  );
};

export default connect(mapStateToProps, mapDispatchProps)(PaymentCreateBeauty);
